import React, {useState} from 'react';
import {Alert, Box, Chip, CircularProgress, Stack, Typography} from '@mui/material';
import {PieChart as PieChartIcon} from '@mui/icons-material';
import {Cell, Pie, PieChart, ResponsiveContainer, Tooltip} from 'recharts';
import {useReportsServiceGetApiReportsTasksStatusSummary} from '../../api/generated/queries/queries';
import WidgetCard from '../DashboardWidgets/WidgetCard';

// Status colors and gradients
const STATUS_CONFIG = {
  TODO: {
    color: '#f59e0b',
    gradientId: 'todoGradient',
    gradientStart: '#fbbf24',
    gradientEnd: '#f59e0b',
    label: 'Do zrobienia'
  },
  IN_PROGRESS: {
    color: '#3b82f6',
    gradientId: 'inProgressGradient',
    gradientStart: '#60a5fa',
    gradientEnd: '#3b82f6',
    label: 'W trakcie'
  },
  CANCELED: {
    color: '#ef4444',
    gradientId: 'canceledGradient',
    gradientStart: '#f87171',
    gradientEnd: '#ef4444',
    label: 'Anulowane'
  },
  DONE: {
    color: '#10b981',
    gradientId: 'doneGradient',
    gradientStart: '#34d399',
    gradientEnd: '#10b981',
    label: 'Zakończone'
  }
};

const TaskStatusSummaryWidget: React.FC = () => {
  const { data: statusSummary, isLoading, error } = useReportsServiceGetApiReportsTasksStatusSummary();
  const [hiddenStatuses, setHiddenStatuses] = useState<Set<string>>(new Set());

  if (isLoading) {
    return (
      <WidgetCard>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
          <CircularProgress size={24} />
        </Box>
      </WidgetCard>
    );
  }

  if (error) {
    return (
      <WidgetCard>
        <Alert severity="error" sx={{ border: 'none', p: 0 }}>
          Błąd podczas ładowania danych raportów.
        </Alert>
      </WidgetCard>
    );
  }

  // Prepare data for the pie chart
  const chartData = statusSummary
    ?.filter(item => item.status && item.count !== undefined && item.count > 0)
    ?.filter(item => !hiddenStatuses.has(item.status!))
    ?.map(item => ({
      name: item.status!,
      value: item.count!,
      label: STATUS_CONFIG[item.status as keyof typeof STATUS_CONFIG]?.label || item.status!,
      color: STATUS_CONFIG[item.status as keyof typeof STATUS_CONFIG]?.color || '#666'
    })) || [];

  const toggleStatus = (status: string) => {
    const newHiddenStatuses = new Set(hiddenStatuses);
    if (newHiddenStatuses.has(status)) {
      newHiddenStatuses.delete(status);
    } else {
      newHiddenStatuses.add(status);
    }
    setHiddenStatuses(newHiddenStatuses);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <Box
          sx={{
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: 1,
            p: 1,
            boxShadow: 2
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {data.payload.label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Liczba zadań: {data.value}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    if (percent < 0.05) return null; // Don't show labels for slices smaller than 5%
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <WidgetCard>
      {/* Header */}
      <Box
        mb={1.5}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pb={1}
        borderBottom="1px solid"
        borderColor="divider"
      >
        <Box display="flex" alignItems="center">
          <Box
            sx={{
              backgroundColor: '#e3f2fd',
              borderRadius: '50%',
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1,
            }}
          >
            <PieChartIcon sx={{ color: '#1976d2', fontSize: 18 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={600} fontSize="0.95rem">
            Statusy zadań
          </Typography>
        </Box>
      </Box>

      {/* Status Toggle Chips */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {statusSummary?.map(item => {
            if (!item.status || !item.count || item.count === 0) return null;
            
            const config = STATUS_CONFIG[item.status as keyof typeof STATUS_CONFIG];
            const isHidden = hiddenStatuses.has(item.status);
            
            return (
              <Chip
                key={item.status}
                label={`${config?.label || item.status} (${item.count})`}
                onClick={() => toggleStatus(item.status!)}
                sx={{
                  backgroundColor: isHidden ? '#f5f5f5' : config?.color + '20',
                  color: isHidden ? '#999' : config?.color,
                  border: `1px solid ${isHidden ? '#ddd' : config?.color}`,
                  '&:hover': {
                    backgroundColor: isHidden ? '#eeeeee' : config?.color + '30',
                  },
                  opacity: isHidden ? 0.5 : 1,
                  textDecoration: isHidden ? 'line-through' : 'none'
                }}
                size="small"
              />
            );
          })}
        </Stack>
      </Box>

      {/* Content - Pie Chart */}
      <Box sx={{ height: 448, mt: 2 }}>
        {chartData.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography variant="body2" color="text.secondary">
              Brak danych do wyświetlenia
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <defs>
                {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                  <linearGradient key={config.gradientId} id={config.gradientId} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={config.gradientStart} />
                    <stop offset="100%" stopColor={config.gradientEnd} />
                  </linearGradient>
                ))}
              </defs>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => {
                  const config = STATUS_CONFIG[entry.name as keyof typeof STATUS_CONFIG];
                  return (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={`url(#${config?.gradientId})`}
                    />
                  );
                })}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Box>
    </WidgetCard>
  );
};

export default TaskStatusSummaryWidget;